// Certificados de formação profissional e declarações de participação, gerados a partir da ação.
// · Formação certificada (isenta/FCT): certificado de formação profissional segundo o modelo da
//   Portaria n.º 474/2010, emitido pela Workwell (formação fora do Catálogo Nacional de Qualificações, sem SIGO).
// · Webinars, workshops e restantes: declaração de participação, só com o nome.
// O HUB abre uma página A4 por participante; «Guardar como PDF» na janela de impressão.
import { ehFormacaoProfissional } from './formacao';

// símbolo de entidade formadora certificada: o da DGERT mudou a 1/10/2025 (descarrega-se no Balcão Único, Registo da Entidade › Logótipo).
// O logótipo atual vem do rodapé dos modelos MO-DFO (o antigo, em «C», já não se usa).
const LOGO_DGERT = 'dgert-logo.png';

export const ehCertificadoOficial = (a) => ehFormacaoProfissional(a) || (a && a.tipo_acao === 'Formação certificada');
export const nomeDocumento = (a) => ehCertificadoOficial(a) ? 'Certificado de Formação Profissional' : 'Declaração de Participação';
export const RESULTADOS = [['aproveitamento', 'concluiu com aproveitamento'], ['frequencia', 'frequentou']];

// campos de cada participante (os do certificado oficial só aparecem nas ações certificadas)
export const CAMPOS_OFICIAIS = [['naturalidade', 'Naturalidade'], ['nascimento', 'Nascimento'], ['documento', 'N.º CC/BI'], ['validade', 'Válido até']];
export const faltaDados = (a, p) => {
  if (!String(p.nome || '').trim()) return ['nome'];
  if (!ehCertificadoOficial(a)) return [];
  return CAMPOS_OFICIAIS.filter(([k]) => !String(p[k] || '').trim()).map(([, t]) => t.toLowerCase());
};

// datas escritas à mão: 05/03/1990, 5-3-1990, 1990-03-05 → 05/03/1990
export const normalizarData = (s) => {
  const t = String(s || '').trim(); if (!t) return '';
  let m = t.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/); if (m) return `${m[1].padStart(2, '0')}/${m[2].padStart(2, '0')}/${m[3]}`;
  m = t.match(/^(\d{4})-(\d{2})-(\d{2})/); if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return t;
};

// colar do Excel: uma linha por pessoa — nome [; naturalidade; nascimento; n.º CC; validade]
export function lerColado(texto) {
  return String(texto || '').split(/\r?\n/).map(l => l.trim()).filter(Boolean).map(l => {
    const c = (l.includes('\t') ? l.split('\t') : l.split(';')).map(x => x.trim());
    return { nome: c[0] || '', naturalidade: c[1] || '', nascimento: normalizarData(c[2]), documento: c[3] || '', validade: normalizarData(c[4]) };
  }).filter(p => p.nome && !/^nome\b/i.test(p.nome));
}

// relatório de presenças do Teams (.csv, normalmente UTF-16 com tabulações): tira os nomes dos participantes
export async function lerRelatorioTeams(file, excluir = []) {
  const buf = await file.arrayBuffer();
  const b = new Uint8Array(buf);
  const texto = (b[0] === 0xFF && b[1] === 0xFE) ? new TextDecoder('utf-16le').decode(buf) : new TextDecoder('utf-8').decode(buf);
  const linhas = texto.replace(/^﻿/, '').split(/\r?\n/);
  const sep = (l) => (l.includes('\t') ? '\t' : l.includes(';') ? ';' : ',');
  const cel = (l) => l.split(sep(l)).map(x => x.replace(/^"|"$/g, '').trim());
  const iCab = linhas.findIndex(l => /^(name|nome|full name|nome completo)$/i.test(cel(l)[0] || ''));
  if (iCab < 0) return [];
  const cab = cel(linhas[iCab]).map(x => x.toLowerCase());
  const iPapel = cab.findIndex(x => /^(role|função|funcao|papel)$/.test(x));
  const fora = excluir.map(x => String(x || '').trim().toLowerCase()).filter(Boolean);
  const nomes = [];
  for (const l of linhas.slice(iCab + 1)) {
    if (!l.trim() || /^\d+\.\s/.test(l.trim())) break;            // fim da secção de participantes
    const c = cel(l); const nome = c[0];
    if (!nome) continue;
    if (iPapel >= 0 && /organi[sz]/i.test(c[iPapel] || '')) continue;  // o organizador não é formando
    if (fora.includes(nome.toLowerCase())) continue;
    if (!nomes.includes(nome)) nomes.push(nome);
  }
  return nomes.map(nome => ({ nome }));
}

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const dataExtenso = (iso) => { const d = new Date((iso || new Date().toISOString()).slice(0, 10) + 'T12:00:00'); return `${String(d.getDate()).padStart(2, '0')} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`; };
const dataCurta = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
export const horasHHMM = (h) => { const n = Number(h) || 0; const hh = Math.floor(n); const mm = Math.round((n - hh) * 60); return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`; };
const horasTexto = (h) => { const n = Number(h) || 0; return n === 1 ? '1 hora' : `${String(n).replace('.', ',')} horas`; };

// página A4 com um documento por participante
export function htmlDocumentos(a, participantes, { cfg = {}, cliente = '', resultado = 'aproveitamento', emitidoEm } = {}) {
  const oficial = ehCertificadoOficial(a);
  const origem = window.location.origin;
  const local = cfg.certificado_local || 'Lisboa';
  const resp = cfg.certificado_responsavel || '';
  const quando = dataExtenso(emitidoEm);
  const res = (RESULTADOS.find(([k]) => k === resultado) || RESULTADOS[0])[1];
  const tipo = (a.tipo_acao || 'ação').toLowerCase();
  const fem = /^(formação|ação)/.test(tipo);   // a formação / o webinar
  const paginas = participantes.map(p => {
    const corpo = oficial
      ? `<p class="txt">Certifica-se que <b>${esc(p.nome)}</b>, natural de ${esc(p.naturalidade)}, nascido(a) em ${esc(p.nascimento)}, com o n.º de identificação civil (CC/BI) ${esc(p.documento)}, válido até ${esc(p.validade)}, ${res} a ação de formação profissional <b>«${esc(a.titulo)}»</b>, em ${dataCurta(a.data)}, com a duração de ${horasHHMM(a.duracao_horas)} horas${a.area_formacao ? `, na área de educação e formação ${esc(a.area_formacao)}` : ''}.</p>
         <table><thead><tr><th>Unidades de formação / módulos / outras designações</th><th>Horas (hh:mm)</th><th>Classificação</th></tr></thead>
         <tbody><tr><td>${esc(a.titulo)}</td><td class="c">${horasHHMM(a.duracao_horas)}</td><td class="c">—</td></tr></tbody></table>`
      : `<p class="txt">Declara-se que <b>${esc(p.nome)}</b> participou ${fem ? 'na' : 'no'} ${esc(tipo)} <b>«${esc(a.titulo)}»</b>, ${fem ? 'realizada' : 'realizado'} em ${dataCurta(a.data)}, com a duração de ${horasTexto(a.duracao_horas)}${a.modalidade ? `, em formato ${esc(String(a.modalidade).toLowerCase())}` : ''}${cliente ? `, no âmbito do programa da ${esc(cliente)}` : ''}, ${fem ? 'promovida' : 'promovido'} pela Workwell.</p>`;
    return `<section class="pag">
      <div class="topo"><img class="logo" src="${origem}/workwell-academy-horizontal.png" alt="Workwell Academy"/>${oficial && LOGO_DGERT ? `<img class="dgert" src="${origem}/${LOGO_DGERT}" alt="DGERT · Entidade formadora certificada"/>` : ''}</div>
      <h1>${oficial ? 'Certificado de Formação Profissional' : 'Declaração de Participação'}</h1>
      ${corpo}
      ${a.formador_nome ? `<p class="sub">Formador(a): ${esc(a.formador_nome)}</p>` : ''}
      <div class="ass">
        <p>${esc(local)}, ${quando}</p>
        <p>O(A) Responsável pela Workwell</p>
        <div class="linha"></div>
        <p>${esc(resp)}</p>
        ${oficial ? '<p class="peq">(Assinatura e carimbo)</p>' : ''}
      </div>
      <footer>${oficial ? `Certificado n.º ${esc(p.cert_numero || '')}, de acordo com o modelo publicado na Portaria n.º 474/2010 · N.º de identificação civil (CC/BI) ${esc(p.documento)}<br/>` : ''}
        Workwell, Unipessoal Lda. · NIPC 508729076${oficial && cfg.dgert_numero ? ` · Entidade formadora certificada pela DGERT (certificado n.º ${esc(cfg.dgert_numero)})` : ''}</footer>
    </section>`;
  }).join('');
  return `<!doctype html><html lang="pt-PT"><head><meta charset="utf-8"/><title>${esc(nomeDocumento(a))} · ${esc(a.titulo)}</title>
<link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=Fraunces:wght@600&display=swap" rel="stylesheet"/>
<style>
  @page { size: A4; margin: 0; }
  body { margin:0; font-family:'DM Sans',Arial,sans-serif; color:#1A1A2E; background:#eee; }
  .pag { width:210mm; height:297mm; box-sizing:border-box; padding:28mm 24mm 22mm; background:#fff; margin:0 auto 10mm; position:relative; page-break-after:always; }
  .topo { display:flex; justify-content:space-between; align-items:center; }
  .logo { height:13mm; } .dgert { height:14mm; }
  h1 { font-family:'Fraunces',Georgia,serif; color:#691FB1; font-size:25pt; margin:14mm 0 12mm; font-weight:600; }
  .txt { font-size:12.5pt; line-height:1.75; text-align:justify; }
  .sub { font-size:10.5pt; color:#4A4A6A; margin-top:6mm; }
  table { width:100%; border-collapse:collapse; margin-top:9mm; font-size:10.5pt; }
  th, td { border:1px solid #CFC6EE; padding:6px 8px; text-align:left; } th { background:#EDE7F6; color:#4A1A82; font-weight:600; } td.c { text-align:center; }
  .ass { position:absolute; left:24mm; right:24mm; bottom:42mm; font-size:11pt; }
  .ass p { margin:2px 0; } .linha { border-bottom:1px solid #1A1A2E; width:75mm; height:16mm; } .peq { font-size:9pt; color:#8888AA; }
  footer { position:absolute; left:24mm; right:24mm; bottom:14mm; font-size:8pt; color:#8888AA; border-top:1px solid #E4DEFA; padding-top:3mm; line-height:1.5; }
  @media print { body { background:#fff; } .pag { margin:0; } .barra { display:none; } }
  .barra { position:sticky; top:0; background:#691FB1; color:#fff; padding:10px 16px; font-size:14px; text-align:center; }
  .barra button { margin-left:12px; padding:6px 14px; border:none; border-radius:6px; font-weight:700; cursor:pointer; }
</style></head><body>
<div class="barra">${participantes.length} ${participantes.length === 1 ? 'documento' : 'documentos'} · na janela de impressão escolhe «Guardar como PDF»<button onclick="window.print()">Imprimir / Guardar PDF</button></div>
${paginas}</body></html>`;
}

export function abrirDocumentos(html, janela) {
  const w = janela || window.open('', '_blank');
  if (!w) { window.alert('O browser bloqueou a janela. Permite pop-ups para o HUB e tenta de novo.'); return; }
  w.document.open(); w.document.write(html); w.document.close();
}

// ───────── PDF (um por participante) e ZIP para enviar ao cliente ─────────
// jsPDF e JSZip só carregam quando se emitem documentos (não pesam no resto do HUB).
const imagemDataURL = async (url) => {
  try {
    const r = await fetch(url); if (!r.ok) return null;
    const b = await r.blob();
    const d = await new Promise((ok) => { const fr = new FileReader(); fr.onload = () => ok(fr.result); fr.onerror = () => ok(null); fr.readAsDataURL(b); });
    const dim = await new Promise((ok) => { const im = new Image(); im.onload = () => ok({ w: im.naturalWidth, h: im.naturalHeight }); im.onerror = () => ok(null); im.src = d; });
    return d && dim ? { d, ...dim } : null;
  } catch (_) { return null; }
};
const nomeFicheiro = (s) => String(s || '').normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^\w]+/g, '_').replace(/^_|_$/g, '').slice(0, 50);

async function recursos(a, cfg, assinaturaURL) {
  const origem = window.location.origin;
  const [logo, ww, dgert, ass] = await Promise.all([
    imagemDataURL(`${origem}/workwell-academy-horizontal.png`),
    imagemDataURL(`${origem}/workwell-horizontal-cor.png`),
    ehCertificadoOficial(a) && LOGO_DGERT ? imagemDataURL(`${origem}/${LOGO_DGERT}`) : null,
    assinaturaURL ? imagemDataURL(assinaturaURL) : null,
  ]);
  return { logo, ww, dgert, ass };
}

function paginaPDF(doc, a, p, { cfg = {}, cliente = '', resultado = 'aproveitamento', emitidoEm, img = {} }) {
  const oficial = ehCertificadoOficial(a);
  const M = 24, L = 210 - 2 * M;
  const roxo = [105, 31, 177], txt = [26, 26, 46], cinza = [136, 136, 170];
  if (img.logo) doc.addImage(img.logo.d, 'PNG', M, 20, 62, 62 * img.logo.h / img.logo.w);
  if (img.dgert) { const h = 14; const w = h * img.dgert.w / img.dgert.h; doc.addImage(img.dgert.d, 'PNG', 210 - M - w, 18, w, h); }
  doc.setFont('helvetica', 'bold'); doc.setFontSize(22); doc.setTextColor(...roxo);
  doc.text(oficial ? 'Certificado de Formação Profissional' : 'Declaração de Participação', M, 56);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(11.5); doc.setTextColor(...txt);
  doc.text(oficial ? 'Certifica-se que' : 'Declara-se que', M, 72);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(16); doc.text(String(p.nome || ''), M, 81);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(11.5);
  const res = (RESULTADOS.find(([k]) => k === resultado) || RESULTADOS[0])[1];
  const tipo = (a.tipo_acao || 'ação').toLowerCase();
  const fem = /^(formação|ação)/.test(tipo);
  const frase = oficial
    ? `natural de ${p.naturalidade}, nascido(a) em ${p.nascimento}, com o n.º de identificação civil (CC/BI) ${p.documento}, válido até ${p.validade}, ${res} a ação de formação profissional «${a.titulo}», em ${dataCurta(a.data)}, com a duração de ${horasHHMM(a.duracao_horas)} horas${a.area_formacao ? `, na área de educação e formação ${a.area_formacao}` : ''}.`
    : `participou ${fem ? 'na' : 'no'} ${tipo} «${a.titulo}», ${fem ? 'realizada' : 'realizado'} em ${dataCurta(a.data)}, com a duração de ${horasTexto(a.duracao_horas)}${a.modalidade ? `, em formato ${String(a.modalidade).toLowerCase()}` : ''}${cliente ? `, no âmbito do programa da ${cliente}` : ''}, ${fem ? 'promovida' : 'promovido'} pela Workwell.`;
  const linhas = doc.splitTextToSize(frase, L);
  doc.text(linhas, M, 91, { lineHeightFactor: 1.6 });
  let y = 91 + linhas.length * 11.5 * 0.3528 * 1.6 + 6;
  if (oficial) {
    const c = [L - 62, 30, 32];
    doc.setFillColor(237, 231, 246); doc.setDrawColor(207, 198, 238); doc.rect(M, y, L, 8, 'FD');
    doc.setFontSize(9.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(74, 26, 130);
    doc.text('Unidades de formação / módulos / outras designações', M + 2, y + 5.3);
    doc.text('Horas (hh:mm)', M + c[0] + c[1] / 2, y + 5.3, { align: 'center' });
    doc.text('Classificação', M + c[0] + c[1] + c[2] / 2, y + 5.3, { align: 'center' });
    const tl = doc.splitTextToSize(String(a.titulo || ''), c[0] - 4);
    const h = Math.max(8, tl.length * 4.6 + 3.5);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...txt); doc.rect(M, y + 8, L, h);
    doc.line(M + c[0], y, M + c[0], y + 8 + h); doc.line(M + c[0] + c[1], y, M + c[0] + c[1], y + 8 + h);
    doc.text(tl, M + 2, y + 13.3);
    doc.text(horasHHMM(a.duracao_horas), M + c[0] + c[1] / 2, y + 13.3, { align: 'center' });
    doc.text('—', M + c[0] + c[1] + c[2] / 2, y + 13.3, { align: 'center' });
    y += 8 + h + 7;
  }
  if (a.formador_nome) { doc.setFontSize(10); doc.setTextColor(74, 74, 106); doc.text(`Formador(a): ${a.formador_nome}`, M, y); }
  // assinatura
  doc.setFontSize(11); doc.setTextColor(...txt);
  doc.text(`${cfg.certificado_local || 'Lisboa'}, ${dataExtenso(emitidoEm)}`, M, 222);
  doc.text('O(A) Responsável pela Workwell', M, 228);
  if (img.ass) { const h = 16; const w = Math.min(70, h * img.ass.w / img.ass.h); doc.addImage(img.ass.d, 'PNG', M + 2, 232, w, w * img.ass.h / img.ass.w); }
  if (img.ww) { const w = 42; doc.addImage(img.ww.d, 'PNG', 210 - M - w, 246, w, w * img.ww.h / img.ww.w); }
  doc.setDrawColor(26, 26, 46); doc.line(M, 250, M + 75, 250);
  doc.text(String(cfg.certificado_responsavel || ''), M, 255);
  if (oficial) { doc.setFontSize(8.5); doc.setTextColor(...cinza); doc.text('(Assinatura e carimbo)', M, 260); }
  // rodapé
  doc.setDrawColor(228, 222, 250); doc.line(M, 274, 210 - M, 274);
  doc.setFontSize(7.5); doc.setTextColor(...cinza);
  const rod = [
    ...(oficial ? [`Certificado n.º ${p.cert_numero || ''}, de acordo com o modelo publicado na Portaria n.º 474/2010 · N.º de identificação civil (CC/BI) ${p.documento}`] : []),
    `Workwell, Unipessoal Lda. · NIPC 508729076${oficial && cfg.dgert_numero ? ` · Entidade formadora certificada pela DGERT (certificado n.º ${cfg.dgert_numero})` : ''}`,
  ];
  doc.text(rod, M, 279, { lineHeightFactor: 1.5 });
}

// Gera os documentos. 1 pessoa → PDF; várias → ZIP com um PDF por pessoa. Devolve o Blob e o nome do ficheiro.
export async function gerarDocumentos(a, participantes, opts = {}) {
  const [{ jsPDF }, img] = await Promise.all([import('jspdf'), recursos(a, opts.cfg || {}, opts.assinaturaURL)]);
  const oficial = ehCertificadoOficial(a);
  const pref = oficial ? 'Certificado' : 'Declaracao';
  const um = (p) => { const doc = new jsPDF({ unit: 'mm', format: 'a4' }); paginaPDF(doc, a, p, { ...opts, img }); return doc; };
  const nomeDe = (p) => `${pref}${oficial && p.cert_numero ? '_' + String(p.cert_numero).replace('/', '-') : ''}_${nomeFicheiro(p.nome)}.pdf`;
  if (participantes.length === 1) return { blob: um(participantes[0]).output('blob'), nome: nomeDe(participantes[0]) };
  const { default: JSZip } = await import('jszip');
  const zip = new JSZip();
  participantes.forEach(p => zip.file(nomeDe(p), um(p).output('arraybuffer')));
  return { blob: await zip.generateAsync({ type: 'blob' }), nome: `${oficial ? 'Certificados' : 'Declaracoes'}_${nomeFicheiro(a.titulo)}_${(a.data || '').slice(0, 10)}.zip` };
}

export function descarregarBlob(blob, nome) {
  const u = URL.createObjectURL(blob); const el = document.createElement('a');
  el.href = u; el.download = nome; document.body.appendChild(el); el.click(); el.remove();
  setTimeout(() => URL.revokeObjectURL(u), 4000);
}
